import { toFlatConfig } from '../domain';
import { quoteConfigToDomain } from '../domain/QuoteConfigMapper';
import { migrateQuoteConfig } from './migration';

/**
 * Resolves a loaded Quote row to a flat config the rest of the app consumes.
 * Structured rows (configV2 present) go configV2 -> domain -> flat (already
 * current schema). Legacy blob rows parse `config` and run the flat migration
 * chain. The two migration paths stay separate by design (spec §4).
 */
export function flatConfigFromQuoteRow(quote) {
  if (quote.configV2) {
    // customPricing / customMetrics are quote-row output data, not config —
    // they must never reach the domain boxes or the flat projection.
    const { customMetrics, ...rest } = quote.configV2;
    const { customPricing, ...pricing } = rest.pricing ?? {};
    return toFlatConfig(quoteConfigToDomain({ ...rest, pricing }));
  }
  return migrateQuoteConfig(JSON.parse(quote.config));
}

/** Outputs snapshot from a structured row, customPrices parsed. Null when the
 *  quote has no custom pricing (or is a legacy row). */
export function customPricingFromQuoteRow(quote) {
  const cp = quote?.configV2?.pricing?.customPricing;
  if (!cp) return null;
  const { __typename, ...rest } = cp;
  return {
    ...rest,
    customPrices: typeof cp.customPrices === 'string'
      ? JSON.parse(cp.customPrices) : (cp.customPrices ?? {}),
  };
}

/** CONF-212. Saved metric overrides from a structured row, parsed. Null when
 *  the quote has none (or is a legacy row). */
export function customMetricsFromQuoteRow(quote) {
  const cm = quote?.configV2?.customMetrics;
  if (!cm) return null;
  const { __typename, ...rest } = cm;
  return {
    ...rest,
    overrides: typeof cm.overrides === 'string' ? JSON.parse(cm.overrides) : (cm.overrides ?? {}),
  };
}
